import styled, { css, keyframes } from 'styled-components';
import { rem } from 'polished';

const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
`;

const floatParticle = keyframes`
  0% { 
    transform: translate(0, 0) scale(1);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% { 
    transform: translate(100px, -100vh) scale(0.5);
    opacity: 0;
  }
`;

export const Section = styled.section`
  padding: ${rem('60px')} 0;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  min-height: 60vh;
  display: flex;
  align-items: center;
`;

export const InnerContainer = styled.div`
  display: grid;
  place-items: center;
  gap: ${rem('35px')};
  grid-template-columns: 1fr 1fr;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  position: relative;
  z-index: 2;

  @media ${({ theme }) => theme.mediaQueries.mediumMax} {
    grid-template-columns: 1fr;
    text-align: center;
    padding: 0 ${rem('16px')};
  }
`;

export const Content = styled.div`
  width: 100%;
  max-width: 100%;
  padding-top: ${rem('60px')};
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

export const HeroText = styled.h1`
  font-family: 'MontserratBlack', sans-serif;
  color: #1a1a1a;
  margin-top: 0;
  margin-bottom: ${rem('20px')};
  font-size: clamp(2.5rem, 4vw, 3.5rem);
  line-height: 1.2;
  letter-spacing: -0.5px;
  font-weight: 800;
  word-wrap: break-word;
  overflow-wrap: break-word;

  @media ${({ theme }) => theme.mediaQueries.mediumMax} {
    font-size: clamp(1.8rem, 7vw, 2.5rem);
  }

  @media ${({ theme }) => theme.mediaQueries.smallMax} {
    font-size: clamp(1.5rem, 6vw, 2rem);
  }
`;

export const HeroDescription = styled.p`
  font-size: ${rem('20px')};
  color: #1a1a1a;
  margin-bottom: ${rem('30px')};
  margin-top: ${rem('10px')};
  line-height: 1.4;
  font-weight: 400;
  max-width: 600px;
  opacity: 0.9;

  @media ${({ theme }) => theme.mediaQueries.mediumMax} {
    font-size: ${rem('16px')};
    max-width: 100%;
    margin-bottom: ${rem('12px')};
  }

  @media ${({ theme }) => theme.mediaQueries.smallMax} {
    font-size: ${rem('15px')};
  }
`;

export const HeroSubText = styled.p`
  font-size: ${rem('14px')};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${rem('15px')};
  margin-top: ${rem('-5px')};
  font-style: italic;
  opacity: 0.8;
  font-weight: 400;

  @media ${({ theme }) => theme.mediaQueries.mediumMax} {
    font-size: ${rem('13px')};
  }
`;

export const HeroSubtitle = styled.p`
  font-size: ${rem('20px')};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${rem('20px')};
  line-height: 1.6;
  font-weight: 400;
  max-width: 500px;

  @media ${({ theme }) => theme.mediaQueries.mediumMax} {
    font-size: ${rem('18px')};
    max-width: 100%;
    margin-bottom: ${rem('30px')};
  }

  @media ${({ theme }) => theme.mediaQueries.smallMax} {
    font-size: ${rem('16px')};
  }
`;

export const HeroImg = styled.img`
  justify-self: right;
  max-height: 50vh;
`;

export const ButtonStyles = css`
  padding: ${rem('18px')} ${rem('32px')};
  border-radius: 8px;
  background: #ffffff;
  color: #1a1a1a;
  font-weight: 600;
  font-size: ${rem('16px')};
  letter-spacing: 0px;
  position: relative;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.1);
  text-transform: none;
  border: 1px solid rgba(0, 0, 0, 0.1);
  cursor: pointer;

  &::after {
    content: '→';
    margin-left: ${rem('8px')};
    font-weight: 400;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.15);
    background: #f8f8f8;
  }

  &:active {
    transform: translateY(0px);
  }
`;

// Particle styles
export const ParticlesContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
`;

export const Particle = styled.div`
  position: absolute;
  font-size: 20px;
  left: ${(props) => props.left || '0%'};
  top: 100%;
  animation: ${floatParticle} ${(props) => props.duration || '10s'} linear
    infinite;
  animation-delay: ${(props) => props.delay || '0s'};
  opacity: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Animated Lottie wrapper
export const AnimationWrapper = styled.div`
  animation: ${floatAnimation} 10s ease-in-out infinite;
  width: 450px;
  height: 320px;
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 40px;

  @media ${({ theme }) => theme.mediaQueries.mediumMax} {
    width: 100%;
    max-width: 400px;
    height: 280px;
    margin: 30px auto 0;
  }

  @media ${({ theme }) => theme.mediaQueries.smallMax} {
    width: 100%;
    max-width: 320px;
    height: 220px;
    margin: 20px auto 0;
  }
`;
